import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getSignedVideoUrl } from '../services/api';
import useAuthStore from '../context/authStore';
import './ProtectedVideoPlayer.css';

/**
 * ProtectedVideoPlayer — مدرسك
 *
 * Security layers:
 *  1. Cloudinary signed URLs (expire 2h, server-verified enrollment)
 *  2. Right-click disabled on entire player
 *  3. Keyboard shortcuts blocked (F12, Ctrl+Shift+I/J/C, Ctrl+U/S/A)
 *  4. DevTools size detection → blur + pause + watermark
 *  5. Visibility API → pause on tab switch to prevent background recording
 *  6. Transparent overlay blocks drag, select, right-click on video surface
 *  7. User watermark (name + uid) burned into overlay — deters screen recording
 *  8. currentSrc overridden to empty string — blocks inspector source copy
 *  9. controlsList: no download, no remote playback, no picture-in-picture
 * 10. disablePictureInPicture + disableRemotePlayback attributes
 * 11. Print media → black screen (CSS)
 * 12. Auto token refresh 5 min before expiry
 */
export default function ProtectedVideoPlayer({ publicId, directUrl, title = 'الدرس' }) {
  const videoRef = useRef(null);
  const wrapRef  = useRef(null);
  const { userDoc } = useAuthStore();

  const [signedUrl, setSignedUrl] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [blocked,   setBlocked]   = useState(false);
  const [blurred,   setBlurred]   = useState(false);
  const [isFocusLost, setFocusLost] = useState(false);
  const [seekFeedback, setSeekFeedback] = useState(null); // 'backward' | 'forward' | null

  const mode     = publicId ? 'cloudinary' : directUrl ? 'direct' : null;
  const isYouTube = directUrl && /youtube\.com|youtu\.be/.test(directUrl);
  const getYtId   = (url) => {
    const m = url?.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
  };
  const ytId = isYouTube ? getYtId(directUrl) : null;

  /* ── 1. Fetch signed URL ── */
  const fetchUrl = useCallback(async () => {
    if (mode !== 'cloudinary') return;
    setLoading(true); setError(null);
    try {
      const res = await getSignedVideoUrl(publicId);
      setSignedUrl(res.data.url);
      // Refresh 5 min before 2h expiry
      setTimeout(fetchUrl, (7200 - 300) * 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'غير مصرح بمشاهدة هذا الدرس');
    } finally { setLoading(false); }
  }, [publicId, mode]);

  useEffect(() => { if (mode === 'cloudinary') fetchUrl(); }, [fetchUrl, mode]);

  /* ── 2. Block right-click ── */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const prevent = (e) => {
      e.preventDefault();
      setBlocked(true);
      setTimeout(() => setBlocked(false), 1800);
    };
    el.addEventListener('contextmenu', prevent);
    return () => el.removeEventListener('contextmenu', prevent);
  }, []);

  /* ── 3. Block keyboard shortcuts ── */
  useEffect(() => {
    const handler = (e) => {
      const k = e.key.toUpperCase();
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      if (e.key === 'F12') { e.preventDefault(); return; }

      // Block Inspector (Ctrl/Cmd + Shift + I/J/C/K)
      if (cmdOrCtrl && e.shiftKey && ['I','J','C','K'].includes(k)) {
        e.preventDefault(); return;
      }

      // Block Source/Save/Print (Ctrl/Cmd + U/S/P/A)
      if (cmdOrCtrl && ['U','S','P','A'].includes(k)) {
        e.preventDefault(); return;
      }

      // Block Mac specific Inspector (Cmd + Option + I/J/C)
      if (isMac && e.metaKey && e.altKey && ['I','J','C'].includes(k)) {
        e.preventDefault(); return;
      }

      // Block PrintScreen & Snipping Tool shortcuts
      if (e.key === 'PrintScreen' || (e.shiftKey && (isMac ? e.metaKey : e.ctrlKey) && e.key === 'S')) {
        e.preventDefault();
        // Temporarily blank the video
        if (videoRef.current) videoRef.current.style.visibility = 'hidden';
        setTimeout(() => { if (videoRef.current) videoRef.current.style.visibility = 'visible'; }, 500);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  /* ── 4. DevTools detection & aggressive debugger trap ── */
  useEffect(() => {
    const THRESHOLD = 160;
    let trapId;
    let lastTime = 0;

    // Aggressive debugger trap
    const debuggerTrap = () => {
      const startTime = performance.now();
      (function() { return false; }['constructor']('debugger')['call']());
      const endTime = performance.now();

      // If debugger is open, the execution will pause, causing a large time difference
      if (endTime - startTime > 100) {
        setBlurred(true);
        if (videoRef.current && !isYouTube) videoRef.current.pause();
      }
      requestAnimationFrame(debuggerTrap);
    };

    const check = () => {
      const open =
        window.outerWidth  - window.innerWidth  > THRESHOLD ||
        window.outerHeight - window.innerHeight > THRESHOLD;

      if (open) {
        setBlurred(true);
        if (videoRef.current && !isYouTube) videoRef.current.pause();
      } else {
        // Reset blur if DevTools are closed and no debugger trap hit
        setBlurred(false);
      }
    };

    const id = setInterval(check, 1000);
    const animId = requestAnimationFrame(debuggerTrap);
    window.addEventListener('resize', check);

    return () => {
      clearInterval(id);
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', check);
    };
  }, [isYouTube]);

  /* ── 5. Pause on tab switch + Focus Guard ── */
  useEffect(() => {
    const handler = () => {
      if (document.hidden && videoRef.current && !isYouTube) {
        videoRef.current.pause();
      }
      // For YouTube, don't use window focus as clicking the iframe triggers blur
      if (!isYouTube) {
        setFocusLost(!document.hasFocus());
      }
    };
    document.addEventListener('visibilitychange', handler);

    const handleBlur = () => { if (!isYouTube) setFocusLost(true); };
    const handleFocus = () => { if (!isYouTube) setFocusLost(false); };

    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handler);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isYouTube]);

  /* ── 8. Disable context menu & Source Guard ── */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const block = e => e.preventDefault();
    video.addEventListener('contextmenu', block);

    // Override currentSrc to prevent source copy from inspector
    Object.defineProperty(video, 'currentSrc', { get: () => '' });

    // Source obfuscation: remove src from DOM after loading
    const handleLoadedMetadata = () => {
      // Note: removing src attribute will stop playback in some browsers if not careful
      // instead we use a non-enumerable property or just keep the attribute obfuscated
    };
    video.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      video.removeEventListener('contextmenu', block);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [signedUrl, directUrl]);

  /* ── Prevent selection globally while player is mounted ── */
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'pvp-no-select';
    style.textContent = `.pvp-outer,.pvp-outer *{user-select:none!important;-webkit-user-select:none!important;}`;
    document.head.appendChild(style);
    return () => document.getElementById('pvp-no-select')?.remove();
  }, []);

  /* ── 14. Clipboard Guard ── */
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      if (e.clipboardData) e.clipboardData.setData('text/plain', 'محتوى محمي — مدرسك 🔒');
    };
    window.addEventListener('copy', handler);
    return () => window.removeEventListener('copy', handler);
  }, []);

  /* ── 13. Double-tap to seek ── */
  const handleSeek = (direction) => {
    if (!videoRef.current) return;
    const amount = direction === 'forward' ? 10 : -10;
    videoRef.current.currentTime = Math.max(0, Math.min(videoRef.current.duration, videoRef.current.currentTime + amount));
    setSeekFeedback(direction);
    setTimeout(() => setSeekFeedback(null), 800);
  };

  /* ── Watermark text ── */
  const watermarkText = userDoc
    ? `${userDoc.name || ''} · ${userDoc.email || userDoc.uid?.slice(0, 8) || ''}`
    : 'مدرسك';

  if (!mode) return <div className="pvp-empty">لا يوجد فيديو لهذا الدرس</div>;

  if (mode === 'cloudinary' && loading)
    return <div className="pvp-loading"><div className="pvp-spinner" /><span>جاري تحميل الفيديو الآمن...</span></div>;

  if (mode === 'cloudinary' && error)
    return <div className="pvp-error"><span>🔒</span><p>{error}</p></div>;

  const [isPlaying, setIsPlaying] = useState(false);

  const toggleYtPlayback = () => {
    const iframe = wrapRef.current?.querySelector('iframe');
    if (iframe) {
      const func = isPlaying ? 'pauseVideo' : 'playVideo';
      iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args: '' }), '*');
      setIsPlaying(!isPlaying);
    }
  };

  /* ── YouTube embed ── */
  if (mode === 'direct' && isYouTube && ytId) {
    // modestbranding=1, rel=0, controls=1 (needed for seek),
    // but we use clipping to hide the top bar (Watch Later / Share)
    // and the bottom-right YouTube link.
    const src = `https://www.youtube.com/embed/${ytId}?modestbranding=1&rel=0&disablekb=1&fs=0&playsinline=1&cc_load_policy=0&iv_load_policy=3&enablejsapi=1`;
    return (
      <div className={`pvp-outer ${blurred || isFocusLost ? 'pvp-blurred' : ''}`} ref={wrapRef}>
        {blocked && <div className="pvp-blocked-notice">⛔ هذا الفيديو محمي</div>}
        {blurred && <DevToolsOverlay />}
        {isFocusLost && !blurred && (
          <div className="pvp-focus-notice" onClick={() => setFocusLost(false)}>
            انقر هنا لمتابعة المشاهدة 🔒
          </div>
        )}
        <div className="pvp-yt-container">
          <div className="pvp-yt-wrapper">
            {/* Decoy element to confuse scrapers */}
            <div style={{ display: 'none' }} className="pvp-data-source" data-src="blob:https://madrasak.eg/decoy-video-stream-0x921" />
            <iframe src={src} title={title} className="pvp-yt-iframe"
              allow="accelerometer; autoplay; encrypted-media; gyroscope"
              sandbox="allow-scripts allow-same-origin allow-presentation"
              referrerPolicy="strict-origin"
            />
          </div>
          {/* Cover the bottom-right YouTube link/logo that appears on pause */}
          <div className="pvp-yt-logo-shield" onContextMenu={e => e.preventDefault()} />
          <div
            className="pvp-overlay"
            style={{ pointerEvents: 'auto', bottom: 0 }}
            onClick={toggleYtPlayback}
            onContextMenu={e => e.preventDefault()}
          />
          {/* Obfuscation decoys */}
          <div className="pvp-pixel-guard" style={{ position: 'absolute', top: Math.random()*10, left: Math.random()*10, width: 1, height: 1, opacity: 0.01 }} />
        </div>
        <Watermark text={watermarkText} />
        <DynamicWatermark text={watermarkText} />
        <div className="pvp-footer">🔒 مدرسك — محتوى حصري محمي بحقوق الملكية</div>
      </div>
    );
  }

  /* ── Direct MP4 / Cloudinary ── */
  const videoSrc = mode === 'cloudinary' ? signedUrl : directUrl;
  const [pointerState, setPointerState] = useState('auto');

  // Cycle pointer events on overlay to make it harder to target the video element
  useEffect(() => {
    const id = setInterval(() => {
      setPointerState(prev => prev === 'auto' ? 'none' : 'auto');
    }, 3000);
    return () => clearInterval(id);
  }, []);

  // 15. DOM Tampering Detection
  useEffect(() => {
    if (!wrapRef.current) return;
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          const overlay = wrapRef.current.querySelector('.pvp-overlay');
          const watermark = wrapRef.current.querySelector('.pvp-watermark');
          const video = wrapRef.current.querySelector('video');

          // Check for removal or modification of critical security components
          if (!overlay || !watermark || (video && (mutation.target === video && mutation.attributeName === 'src'))) {
            setBlocked(true);
            if (videoRef.current) videoRef.current.pause();
            toast.error('تم اكتشاف تلاعب في مشغل الفيديو ⛔');
          }
        }
      }
    });
    observer.observe(wrapRef.current, { attributes: true, childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // 16. Print Guard
  useEffect(() => {
    const handleBeforePrint = () => { if (videoRef.current) videoRef.current.style.display = 'none'; };
    const handleAfterPrint = () => { if (videoRef.current) videoRef.current.style.display = 'block'; };
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  return (
    <div className={`pvp-outer ${blurred || isFocusLost ? 'pvp-blurred' : ''}`} ref={wrapRef}>
      {blocked && <div className="pvp-blocked-notice">⛔ هذا الفيديو محمي ولا يمكن نسخه</div>}
      {blurred && <DevToolsOverlay />}
      {isFocusLost && !blurred && <div className="pvp-focus-notice">انقر هنا لمتابعة المشاهدة 🔒</div>}

      <div className="pvp-video-container">
        {/* Source obfuscation: randomized ID and decoy attributes */}
        <video
          ref={videoRef}
          src={videoSrc}
          id={`v-stream-${Math.random().toString(36).slice(2, 9)}`}
          className="pvp-video"
          controls
          controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
          disablePictureInPicture
          disableRemotePlayback
          playsInline
          preload="metadata"
          onContextMenu={e => e.preventDefault()}
          onDragStart={e => e.preventDefault()}
          data-security-level="ultra-high"
          data-integrity-hash="sha512-protected-content"
        />

        {/* Double-tap seek overlays (disabled for YouTube iframes) */}
        {!isYouTube && (
          <>
            <div
              className="pvp-seek-area left"
              onDoubleClick={() => handleSeek('backward')}
              onTouchEnd={(e) => {
                if (e.detail === 2) handleSeek('backward');
              }}
            >
              {seekFeedback === 'backward' && (
                <div className="pvp-seek-feedback">
                  <span className="seek-icon">⏪</span>
                  <span className="seek-text">10ث</span>
                </div>
              )}
            </div>
            <div
              className="pvp-seek-area right"
              onDoubleClick={() => handleSeek('forward')}
              onTouchEnd={(e) => {
                if (e.detail === 2) handleSeek('forward');
              }}
            >
              {seekFeedback === 'forward' && (
                <div className="pvp-seek-feedback">
                  <span className="seek-icon">⏩</span>
                  <span className="seek-text">10ث</span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Transparent shield — pointer-events cycling to deter automation/targeting */}
        <div
          className="pvp-overlay"
          style={{ pointerEvents: pointerState }}
          onClick={() => {
            if (videoRef.current) {
              if (videoRef.current.paused) videoRef.current.play().catch(() => {});
              else videoRef.current.pause();
            }
          }}
          onContextMenu={e => e.preventDefault()}
          onDragStart={e => e.preventDefault()}
        />
      </div>

      <Watermark text={watermarkText} />
      <DynamicWatermark text={watermarkText} />
      <div className="pvp-footer">
        {mode === 'cloudinary'
          ? '🔒 فيديو محمي — رابط مشفر مؤقت · مدرسك'
          : '🔒 محتوى حصري لأعضاء مدرسك — لا تشاركه'
        }
      </div>
    </div>
  );
}

/* ── Dynamic (Moving) Watermark ── */
function DynamicWatermark({ text }) {
  const [pos, setPos] = useState({ top: '20%', left: '20%' });

  useEffect(() => {
    const move = () => {
      const t = Math.floor(Math.random() * 60) + 10;
      const l = Math.floor(Math.random() * 60) + 10;
      setPos({ top: `${t}%`, left: `${l}%` });
    };
    const id = setInterval(move, 8000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="pvp-dynamic-wm" style={{ ...pos }}>
      {text}
    </div>
  );
}

/* ── Watermark component ── */
function Watermark({ text }) {
  return (
    <div className="pvp-watermark" aria-hidden="true">
      {Array.from({ length: 12 }).map((_, i) => (
        <span key={i} className="pvp-wm-item">{text}</span>
      ))}
    </div>
  );
}

/* ── DevTools overlay ── */
function DevToolsOverlay() {
  return (
    <div className="pvp-devtools-overlay">
      <span>🔒</span>
      <p>أغلق أدوات المطور لمتابعة المشاهدة</p>
    </div>
  );
}
